import React from 'react';
import styled from 'styled-components';
import MainImg from '../../../assets/images/respaldo.png';
import useDesignUtils from '../../../hooks/useDesignUtils';
import { Box, Copy } from '../../SimpleUI';
import SectionLayout from '../SectionLayout';
import { useCountry } from '../../../contexts/CountryContext';

const Line = styled(Box)`
  position: absolute;
  height: 1px;
  background: #845cff;
  &:before {
    content: '';
    width: 10px;
    height: 10px;
    display: block;
    position: absolute;
    background: #845cff;
    border-radius: 50%;
    top: -5px;
  }
  @media (max-width: 768px) {
    transform: rotate(90deg);
  }
`;

const LeftLine = styled(Line)`
  &:before {
    left: -10px;
  }
`;

const RightLine = styled(Line)`
  &:before {
    right: -10px;
  }
`;

const textsByCountry = {
  portafolio: {
    '/ar': (
      <Copy fontSize="copy">
        Accedés con un solo clic a un portafolio de{' '}
        <Copy fontWeight="semibold" fontSize="copy" tag="span">
          más de 100 propiedades,{' '}
        </Copy>
        mitigando riesgos al invertir en múltiples proyectos simultáneamente.
      </Copy>
    ),
    '/mx': (
      <Copy fontSize="copy">
        <Copy fontWeight="semibold" fontSize="copy" tag="span">
          Accede con un solo clic a un portafolio de más de 100 propiedades,{' '}
        </Copy>
        lo que te permite reducir riesgos al invertir en múltiples proyectos al
        mismo tiempo.
      </Copy>
    ),
  },
  desarrollos: {
    '/ar': (
      <Copy fontSize="copy">
        Invertimos en{' '}
        <Copy tag="span" fontWeight="semibold" fontSize="copy">
          los desarrollos más prestigiosos del mercado,
        </Copy>{' '}
        para darte la mejor opción de inversión.
      </Copy>
    ),
    '/mx': (
      <Copy fontSize="copy">
        Invertimos en{' '}
        <Copy tag="span" fontWeight="semibold" fontSize="copy">
          los desarrollos más prestigiosos del mercado,
        </Copy>{' '}
        para darte la mejor opción de inversión.
      </Copy>
    ),
  },
  ahorros: {
    '/ar': (
      <Copy fontSize="copy">
        <Copy tag="span" fontWeight="semibold" fontSize="copy">
          Tus ahorros están asegurados
        </Copy>{' '}
        en inmuebles de Estados Unidos y Uruguay.
      </Copy>
    ),
    '/mx': (
      <Copy fontSize="copy">
        Tus ahorros están asegurados{' '}
        <Copy tag="span" fontWeight="semibold" fontSize="copy">
          en inmuebles de Estados Unidos.
        </Copy>
      </Copy>
    ),
  },
  analisis: {
    '/ar': (
      <Copy fontSize="copy">
        Nuestro equipo realiza análisis exhaustivos previos a la compra de cada
        uno de los proyectos inmobiliarios que componen nuestro pool de
        propiedades,{' '}
        <Copy tag="span" fontSize="copy" fontWeight="semibold">
          garantizando un sólido respaldo a tu inversión
        </Copy>{' '}
        a través de las mismas.
      </Copy>
    ),
    '/mx': (
      <Copy fontSize="copy">
        Nuestro equipo realiza análisis exhaustivos  previos a la compra de cada
        uno de los proyectos inmobiliarios,{' '}
        <Copy tag="span" fontSize="copy" fontWeight="semibold">
          garantizando un sólido respaldo a tu inversión a través de los mismos.
        </Copy>
      </Copy>
    ),
  },
};

const DesktopView = (countryPrefix) => (
  <Box
    width="100%"
    margin="auto"
    position="relative"
    textAlign="center"
    minHeight="300px"
  >
    <Box width="32%" height="100%" position="absolute" top="50px">
      <LeftLine width="150px" top="10%" right="0" />
      <LeftLine width="140px" bottom="45%" right="7%" />
      <Box
        position="absolute"
        top="15%"
        right="10%"
        maxWidth="300px"
        textAlign="justify"
      >
        {textsByCountry.portafolio[countryPrefix]}
      </Box>
      <Box
        position="absolute"
        bottom="26%"
        right="10%"
        maxWidth="250px"
        textAlign="justify"
      >
        {textsByCountry.desarrollos[countryPrefix]}
      </Box>
    </Box>
    <Box src={MainImg} tag="img" alt="Tu dinero respaldado" maxWidth="450px" />
    <Box width="32%" height="100%" position="absolute" top="50px" right="0">
      <RightLine width="100px" bottom="35%" left="-2%" />
      <RightLine width="70px" top="15%" left="-1%" />
      <Box
        position="absolute"
        top="20%"
        left="10%"
        maxWidth="220px"
        textAlign="justify"
      >
        {textsByCountry.ahorros[countryPrefix]}
      </Box>
      <Box
        position="absolute"
        bottom="10%"
        left="10%"
        maxWidth="360px"
        textAlign="justify"
      >
        {textsByCountry.analisis[countryPrefix]}
      </Box>
    </Box>
  </Box>
);

const MobileView = (countryPrefix) => (
  <Box width="100%" margin="auto" position="relative" textAlign="center">
    <Box
      width="100%"
      height="300px"
      position="relative"
      margin="auto"
      maxWidth="450px"
    >
      <LeftLine width="240px" top="70%" left="-85px" />
      <LeftLine width="110px" bottom="15%" left="10%" />
      <Box
        position="absolute"
        top="10%"
        left="15%"
        maxWidth="300px"
        textAlign="left"
      >
        {textsByCountry.portafolio[countryPrefix]}
      </Box>
      <Box
        position="absolute"
        top="50%"
        left="30%"
        maxWidth="250px"
        textAlign="left"
      >
        {textsByCountry.desarrollos[countryPrefix]}
      </Box>
    </Box>
    <Box
      maxWidth="400px"
      width="100%"
      src={MainImg}
      tag="img"
      alt="Tu dinero respaldado"
    />
    <Box
      width="100%"
      height="300px"
      maxWidth="450px"
      position="relative"
      margin="auto"
    >
      <RightLine width="240px" top="10%" left="-85px" />
      <RightLine width="60px" top="0" left="15%" />
      <Box
        position="absolute"
        top="55%"
        left="6%"
        maxWidth="340px"
        textAlign="left"
      >
        {textsByCountry.analisis[countryPrefix]}
      </Box>
      <Box
        position="absolute"
        top="15%"
        left="20%"
        maxWidth="250px"
        textAlign="left"
      >
        {textsByCountry.ahorros[countryPrefix]}
      </Box>
    </Box>
  </Box>
);

const Infography = () => {
  const { isMobile } = useDesignUtils();
  const { countryPrefix } = useCountry();

  const header =
    countryPrefix === '/ar' ? (
      <>
        <Copy
          tag="span"
          color="_lightPurple"
          fontSize="midHeader"
          fontWeight="bold"
        >
          Simplestate
        </Copy>{' '}
        está respaldado y tu dinero asegurado.
      </>
    ) : (
      <>
        Respáldate{' '}
        <Copy
          tag="span"
          color="_lightPurple"
          fontSize="midHeader"
          fontWeight="bold"
        >
          en ladrillos
        </Copy>
      </>
    );

  return (
    <SectionLayout paddingY="medium" backgroundColor="white">
      <Box
        width="100%"
        paddingX="medium"
        maxWidth="1200px"
        margin="auto"
        marginY="xxxlarge"
        marginBottom="none"
      >
        <Box margin="auto" maxWidth="780px" marginBottom="large">
          <Copy
            tag="h2"
            fontSize="midHeader"
            fontWeight="bold"
            textAlign="center"
          >
            {header}
          </Copy>
        </Box>
        {isMobile ? MobileView(countryPrefix) : DesktopView(countryPrefix)}
      </Box>
    </SectionLayout>
  );
};

export { Infography };
